import { create } from 'zustand';

interface GridItem {
  Name: string;
  IsFolder: boolean;
  [key: string]: unknown;
}

interface NavigationState {
  currentPath: string;
  breadcrumbs: { Name: string; Path: string }[];
  selectedItems: GridItem[]; // Array of selected grid items
  navigateToFolder: (path: string) => void;
  navigateToBreadcrumb: (index: number) => void;
  setSelectedItems: (items: GridItem[]) => void;
  toggleItemSelection: (itemId: string) => void;
  clearSelection: () => void;
  reset: () => void;
}

export const useNavigationStore = create<NavigationState>((set) => ({
  currentPath: '',
  breadcrumbs: [{ Name: 'Root', Path: '' }],
  selectedItems: [],

  navigateToFolder: (path: string) => {
    set(() => {
      // Handle undefined or null path
      if (!path) {
        // Invalid path provided - return current state unchanged
        return {};
      }

      // Remove leading slash if present
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;

      // Build breadcrumbs from path
      const pathParts = cleanPath ? cleanPath.split('/') : [];
      const newBreadcrumbs: { Name: string; Path: string }[] = [
        { Name: 'Root', Path: '' },
      ];

      let currentPath = '';
      pathParts.forEach((part) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        newBreadcrumbs.push({
          Name: part,
          Path: currentPath,
        });
      });

      return {
        currentPath: cleanPath,
        breadcrumbs: newBreadcrumbs,
        selectedItems: [], // Clear selection when navigating
      };
    });
  },

  navigateToBreadcrumb: (index: number) => {
    set((state) => {
      const newBreadcrumbs = state.breadcrumbs.slice(0, index + 1);
      const newPath = newBreadcrumbs[newBreadcrumbs.length - 1].Path;

      return {
        currentPath: newPath,
        breadcrumbs: newBreadcrumbs,
        selectedItems: [], // Clear selection when navigating
      };
    });
  },

  setSelectedItems: (items: GridItem[]) => {
    set({ selectedItems: items });
  },

  toggleItemSelection: (itemId: string) => {
    set((state) => {
      const isSelected = state.selectedItems.some((item) => item.Id === itemId);
      if (isSelected) {
        const newSelectedItems = state.selectedItems.filter((item) => item.Id !== itemId);
        return { selectedItems: newSelectedItems };
      }
      // No-op if not selected; omit else
      return state;
    });
  },
 
  clearSelection: () => {
    set({ selectedItems: [] });
  },

  reset: () => {
    set({
      currentPath: '',
      breadcrumbs: [{ Name: 'Root', Path: '' }],
      selectedItems: [],
    });
  },
}));
